import type { components } from "@brandex/api-client";
import { useBrandex } from "@brandex/dashboard";
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, Select, Spinner } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Trash2 } from "lucide-react";
import { useState } from "react";

import { useWebsites } from "../data";
import { QueryError, fieldError } from "../views";
import { currentPeriod } from "./pipeline";
import { channelLabel, PLATFORMS, type Account, type Platform } from "./publish";

export { channelLabel } from "./publish";

type Deliverable = components["schemas"]["DeliverableDto"];

const logKey = ["admin", "publications"] as const;
/**
 * Publishing log (F-12): record where approved content went live. Only
 * approved items can be logged (the server enforces it); the first
 * publication moves an item to Published.
 */
export function PublishingPage() {
  const { api, platform: shell } = useBrandex();
  const qc = useQueryClient();
  const clients = useWebsites();
  const [org, setOrg] = useState("");
  const [channel, setChannel] = useState<"" | Platform>("");
  const [period, setPeriod] = useState(currentPeriod);
  const log = useQuery({
    queryKey: [...logKey, org, channel, period],
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/publications", {
        params: { query: { period, ...(org === "" ? {} : { org_id: org }), ...(channel === "" ? {} : { platform: channel }) } },
      });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      await api.http.DELETE("/v1/admin/publications/{publication_id}", { params: { path: { publication_id: id } } });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: logKey });
      void qc.invalidateQueries({ queryKey: ["admin", "pipeline"] });
    },
  });
  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Publishing log</h1>
        <p className="mt-1 text-sm text-muted-foreground">Where approved content went live. Nothing can be logged before the doctor approves it.</p>
      </header>
      <LogForm clients={clients.data ?? []} />
      <div className="flex flex-wrap items-start gap-3">
        <Field label="Client">
          <Select className="min-w-48" value={org} onChange={(e) => setOrg(e.target.value)}>
            <option value="">All clients</option>
            {(clients.data ?? []).map((c) => <option key={c.organization_id} value={c.organization_id}>{c.doctor_name ?? c.organization_id}</option>)}
          </Select>
        </Field>
        <Field label="Channel">
          <Select className="min-w-48" value={channel} onChange={(e) => setChannel(e.target.value as "" | Platform)}>
            <option value="">All channels</option>
            {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
        </Field>
        <Field label="Month">
          <Input type="month" className="w-40" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} />
        </Field>
      </div>
      {log.isPending ? <Spinner label="Loading…" /> : null}
      {log.isError ? <QueryError error={log.error} /> : null}
      {remove.isError ? <QueryError error={remove.error} /> : null}
      {log.data !== undefined ? (
        <div className="glass overflow-x-auto rounded-[var(--radius-lg)]">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-3">Item</th><th>Client</th><th>Channel</th><th>Published</th><th className="px-4" /></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {log.data.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Nothing published in this period.</td></tr>
              ) : (
                log.data.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 font-medium">{p.title}</td>
                    <td>{p.doctor_name ?? "—"}</td>
                    <td>{channelLabel(p.platform, p.account)}</td>
                    <td>{p.published_at.slice(0, 10)}</td>
                    <td className="px-4 text-right">
                      <Button size="sm" variant="ghost" aria-label={`Open ${p.title} on ${channelLabel(p.platform, p.account)}`} onClick={() => void shell.openExternal(p.url)}>
                        <ExternalLink className="h-4 w-4" aria-hidden />
                      </Button>
                      <Button size="sm" variant="ghost" aria-label={`Remove ${p.title} on ${channelLabel(p.platform, p.account)}`} loading={remove.isPending && remove.variables === p.id} onClick={() => remove.mutate(p.id)}>
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function LogForm({ clients }: { clients: { organization_id: string; doctor_name?: string | null }[] }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [org, setOrg] = useState("");
  const [item, setItem] = useState("");
  const [platform, setPlatform] = useState<Platform>("facebook");
  const [account, setAccount] = useState<Account>("doctors_diary");
  const [url, setUrl] = useState("");
  const [date, setDate] = useState("");
  // Approved or already-published items of the chosen client, any month.
  const items = useQuery({
    queryKey: ["admin", "publishable", org],
    enabled: org !== "",
    queryFn: async () => {
      const get = async (status: "approved" | "published") =>
        (await api.http.GET("/v1/admin/deliverables", { params: { query: { org_id: org, status } } })).data ?? [];
      const [approved, published] = await Promise.all([get("approved"), get("published")]);
      return [...approved, ...published];
    },
  });
  const save = useMutation({
    mutationFn: async () => {
      const { data } = await api.http.POST("/v1/admin/publications", {
        body: {
          deliverable_id: item,
          platform,
          account: platform === "website" ? "doctor" : account,
          url: url.trim(),
          // A date picks noon in Dhaka that day, so it never shifts a day.
          published_at: date === "" ? null : `${date}T12:00:00+06:00`,
        },
      });
      return data;
    },
    onSuccess: () => {
      setUrl("");
      setItem("");
      void qc.invalidateQueries({ queryKey: logKey });
      void qc.invalidateQueries({ queryKey: ["admin", "publishable"] });
      void qc.invalidateQueries({ queryKey: ["admin", "pipeline"] });
    },
  });
  const ready = item !== "" && url.trim().startsWith("https://");
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Log a publication</CardTitle></CardHeader>
      <CardContent>
        <form className="grid gap-3 md:grid-cols-3" onSubmit={(e) => { e.preventDefault(); if (ready) save.mutate(); }}>
          <Field label="Client for the new publication">
            <Select className="min-w-48" value={org} onChange={(e) => { setOrg(e.target.value); setItem(""); }}>
              <option value="">Choose a client</option>
              {clients.map((c) => <option key={c.organization_id} value={c.organization_id}>{c.doctor_name ?? c.organization_id}</option>)}
            </Select>
          </Field>
          <Field label="Approved item">
            <Select className="min-w-48" value={item} disabled={org === ""} onChange={(e) => setItem(e.target.value)}>
              <option value="">{items.data?.length === 0 ? "No approved items yet" : "Choose an item"}</option>
              {(items.data ?? []).map((d: Deliverable) => (
                <option key={d.id} value={d.id}>{d.title} ({d.status === "published" ? "published" : "approved"})</option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Platform">
              <Select className="min-w-48" value={platform} onChange={(e) => setPlatform(e.target.value as Platform)}>
                {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </Select>
            </Field>
            <Field label="Account">
              <Select className="min-w-48" value={platform === "website" ? "doctor" : account} disabled={platform === "website"} onChange={(e) => setAccount(e.target.value as Account)}>
                <option value="doctors_diary">Doctors Diary</option>
                <option value="doctor">Doctor's own</option>
              </Select>
            </Field>
          </div>
          <Field label="Post link" className="md:col-span-2" error={fieldError(save.error, "url")}>
            <Input type="url" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
          </Field>
          <Field label="Published on" hint="Leave empty for now" error={fieldError(save.error, "published_at", "published_on")}>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          {save.isError ? <div className="md:col-span-3"><QueryError error={save.error} /></div> : null}
          {save.isSuccess ? <div className="md:col-span-3"><Alert tone="success">Logged.</Alert></div> : null}
          <div className="md:col-span-3"><Button type="submit" disabled={!ready} loading={save.isPending}>Log publication</Button></div>
        </form>
      </CardContent>
    </Card>
  );
}
